/**
 * @file citations.ts
 * Utility functions for inline citation processing.
 *
 * Two main functions:
 * 1. `numberSources` — Assigns 1-based citation indices to sources BEFORE the Bedrock call.
 *    Also builds the numbered source block to inject into the system prompt.
 * 2. `buildCitationMapFromSources` — Builds a CitationMap from sources that already have
 *    citationIndex assigned (e.g. from Lambda). This is the primary path used in production.
 */

import { CITATIONS_CONFIG } from '@/lib/config/citations.config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SourceWithCitation {
  /** Original index in the sources array */
  originalIndex: number;
  /** 1-based citation number (null if excluded) */
  citationIndex: number | null;
  type: string;
  title?: string | null;
  content?: string | null;
  url?: string | null;
  docUrl?: string | null;
  pageUrl?: string | null;
  pageSource?: string | null;
  pageTitle?: string | null;
  filename?: string | null;
  isModified?: boolean | null;
  reference?: string | null;
  nomsDesPartis?: string | null;
  filledBy?: string[] | null;
  id?: string;
  /** Tab key this source belongs to: 'laws', 'documents', 'decisions', 'forms' */
  tabKey: string;
}

export interface CitationMap {
  /** Map from citation number (1-based) to source metadata */
  [citationNumber: number]: SourceWithCitation;
}

export interface NumberSourcesResult {
  /** Sources with citationIndex assigned */
  numberedSources: SourceWithCitation[];
  /** Lookup map: citationNumber → source info */
  citationMap: CitationMap;
  /** Text block to append to the system prompt listing numbered sources */
  sourceBlock: string;
  /** Total citation count */
  totalCitations: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Determines which tab key a source type maps to.
 */
function getTabKey(
  sourceType: string,
  isModified?: boolean | null
): string {
  switch (sourceType) {
    case 'LAW':
    case 'MODIFIED_LAW':
      return 'laws';
    case 'INFO':
      return 'documents';
    case 'CASELAW':
      return 'decisions';
    case 'FORM':
      return 'forms';
    default:
      return 'documents';
  }
}

/**
 * Returns a display label for a source to include in the system prompt.
 */
function getSourceLabel(source: SourceWithCitation): string {
  const typeLabel = source.type === 'CASELAW' ? 'Case Law'
    : source.type === 'LAW' ? 'Legislation'
    : source.type === 'MODIFIED_LAW' ? 'Modified Legislation'
    : source.type === 'FORM' ? 'Legal Form'
    : 'Document';

  if (source.type === 'CASELAW') {
    const name = source.reference || source.title || 'Case';
    return `(${typeLabel}) ${name}`;
  }
  if (source.type === 'FORM') {
    const name = source.title || source.filename || 'Form';
    return `(${typeLabel}) ${name}`;
  }
  const name = source.pageTitle || source.title || source.url || 'Source';
  const site = source.pageSource ? ` — ${source.pageSource}` : '';
  return `(${typeLabel}) ${name}${site}`;
}

/**
 * Checks if a source should be excluded from citation numbering.
 */
function isSourceExcluded(source: {
  type: string;
  url?: string | null;
  pageUrl?: string | null;
  docUrl?: string | null;
  pageSource?: string | null;
  title?: string | null;
}): boolean {
  const { EXCLUDED_LINK_PATTERNS, EXCLUDED_TITLE_PATTERNS, EXCLUDED_SOURCE_TYPES } =
    CITATIONS_CONFIG;

  if (EXCLUDED_SOURCE_TYPES.includes(source.type)) {
    return true;
  }

  if (EXCLUDED_LINK_PATTERNS.length > 0) {
    const urls = [source.url, source.pageUrl, source.docUrl, source.pageSource].filter(
      Boolean
    ) as string[];
    for (const pattern of EXCLUDED_LINK_PATTERNS) {
      if (urls.some((u) => u.toLowerCase().includes(pattern.toLowerCase()))) {
        return true;
      }
    }
  }

  if (EXCLUDED_TITLE_PATTERNS.length > 0 && source.title) {
    for (const pattern of EXCLUDED_TITLE_PATTERNS) {
      if (source.title.toLowerCase().includes(pattern.toLowerCase())) {
        return true;
      }
    }
  }

  return false;
}

// ---------------------------------------------------------------------------
// Main Functions
// ---------------------------------------------------------------------------

/**
 * Assigns 1-based citation indices to an array of sources.
 * Sources matching exclusion rules get `citationIndex: null`.
 *
 * Also builds a text block for injection into the Bedrock system prompt,
 * listing each numbered source so the LLM can reference them with `[N]`.
 *
 * @param sources - Array of source objects from the Lambda response
 * @returns NumberSourcesResult with numbered sources, citation map, and prompt block
 */
export function numberSources(
  sources: Array<{
    id?: string;
    type: string;
    title?: string | null;
    content?: string | null;
    url?: string | null;
    docUrl?: string | null;
    pageUrl?: string | null;
    pageSource?: string | null;
    pageTitle?: string | null;
    filename?: string | null;
    isModified?: boolean | null;
    reference?: string | null;
    nomsDesPartis?: string | null;
    filledBy?: string[] | null;
  }>
): NumberSourcesResult {
  const numberedSources: SourceWithCitation[] = [];
  const citationMap: CitationMap = {};
  let citationCounter = 0;

  for (let i = 0; i < sources.length; i++) {
    const source = sources[i];
    const tabKey = getTabKey(source.type, source.isModified);
    const excluded = isSourceExcluded(source);

    let citationIndex: number | null = null;
    if (!excluded && citationCounter < CITATIONS_CONFIG.MAX_CITATIONS) {
      citationCounter++;
      citationIndex = citationCounter;
    }

    const numbered: SourceWithCitation = {
      originalIndex: i,
      citationIndex,
      type: source.type,
      title: source.title,
      content: source.content,
      url: source.url,
      docUrl: source.docUrl,
      pageUrl: source.pageUrl,
      pageSource: source.pageSource,
      pageTitle: source.pageTitle,
      filename: source.filename,
      isModified: source.isModified,
      reference: source.reference,
      nomsDesPartis: source.nomsDesPartis,
      filledBy: source.filledBy,
      id: source.id,
      tabKey,
    };

    numberedSources.push(numbered);

    if (citationIndex !== null) {
      citationMap[citationIndex] = numbered;
    }
  }

  const sourceLines = numberedSources
    .filter((s) => s.citationIndex !== null)
    .map((s) => {
      const label = getSourceLabel(s);
      const snippet = s.content
        ? ` — "${s.content.slice(0, 150).replace(/\n/g, ' ').trim()}${s.content.length > 150 ? '...' : ''}"`
        : '';
      return `[${s.citationIndex}] ${label}${snippet}`;
    })
    .join('\n');

  const sourceBlock = sourceLines
    ? `\n\n---\nAvailable Sources (use [N] to cite inline):\n${sourceLines}\n---`
    : '';

  return {
    numberedSources,
    citationMap,
    sourceBlock,
    totalCitations: citationCounter,
  };
}

/**
 * Builds a CitationMap directly from sources that already have citationIndex assigned
 * (e.g., from Lambda). This is the primary path — Lambda controls numbering, the frontend
 * only renders badges and handles scroll-to-source.
 *
 * @param sources - Array of source objects with pre-assigned citationIndex
 * @returns CitationMap keyed by citation number
 */
export function buildCitationMapFromSources(
  sources: Array<{
    citationIndex?: number | null;
    type: string;
    title?: string | null;
    content?: string | null;
    url?: string | null;
    docUrl?: string | null;
    pageUrl?: string | null;
    pageSource?: string | null;
    pageTitle?: string | null;
    filename?: string | null;
    isModified?: boolean | null;
    reference?: string | null;
    nomsDesPartis?: string | null;
    filledBy?: string[] | null;
    id?: string;
  }>
): CitationMap {
  const map: CitationMap = {};
  for (let i = 0; i < sources.length; i++) {
    const source = sources[i];
    const idx = source.citationIndex;
    if (idx != null && !isSourceExcluded(source)) {
      map[idx] = {
        originalIndex: i,
        citationIndex: idx,
        type: source.type,
        title: source.title,
        content: source.content,
        url: source.url,
        docUrl: source.docUrl,
        pageUrl: source.pageUrl,
        pageSource: source.pageSource,
        pageTitle: source.pageTitle,
        filename: source.filename,
        isModified: source.isModified,
        reference: source.reference,
        nomsDesPartis: source.nomsDesPartis,
        filledBy: source.filledBy,
        id: source.id,
        tabKey: getTabKey(source.type, source.isModified),
      };
    }
  }
  return map;
}

/**
 * Citation instruction text to append to the system prompt.
 */
export const CITATION_INSTRUCTIONS = `
IMPORTANT - Inline Citations:
You MUST cite sources inline using bracket notation like [1], [2], [3], etc.
Rules:
- Place citation numbers immediately after EVERY fact, statement, or piece of information.
- When MULTIPLE sources support the same statement, cite ALL of them together like [1][3][5].
- Cite even when paraphrasing or summarizing.
- Only use citation numbers from the "Available Sources" list below.
- Do NOT list sources at the end — they are shown separately in the UI.
`;

/**
 * Builds the citation-augmented system prompt.
 */
export function buildCitationPrompt(
  originalPrompt: string,
  sourceBlock: string
): string {
  if (!sourceBlock) return originalPrompt;
  return `${originalPrompt}\n${CITATION_INSTRUCTIONS}${sourceBlock}`;
}

/**
 * Extracts all citation numbers from a text string.
 */
export function extractCitationNumbers(text: string): number[] {
  const matches = text.matchAll(CITATIONS_CONFIG.CITATION_REGEX);
  const numbers = new Set<number>();
  for (const match of matches) {
    numbers.add(parseInt(match[1], 10));
  }
  return Array.from(numbers).sort((a, b) => a - b);
}
